"use client";

import { Activity, ArrowRight, Baby, HeartPulse, Microscope, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  icon: Activity,
  title: "Deep Cleaning",
  description: "A comprehensive, top-to-bottom refresh of your home, targeting hidden dust and grime for a truly pristine living space."
}, {
  icon: Baby,
  title: "Recurring Maid Service",
  description: "Consistent, reliable maintenance cleaning tailored to your schedule, ensuring your home stays spotless week after week."
}, {
  icon: HeartPulse,
  title: "Move-In/Move-Out",
  description: "Detailed cleaning services designed to make your transition seamless, leaving your old or new home sparkling clean."
}, {
  icon: Activity,
  title: "Kitchen & Bath Detail",
  description: "Specialized focus on high-traffic areas, sanitizing surfaces and polishing fixtures to a brilliant, hygienic shine."
}, {
  icon: ShieldCheck,
  title: "Eco-Friendly Care",
  description: "Safe, non-toxic cleaning solutions that protect your family and pets while delivering exceptional results."
}, {
  icon: Microscope,
  title: "Customized Add-ons",
  description: "Tailor your service with specific requests like interior window cleaning, oven detailing, or laundry assistance."
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof services[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = service.icon;
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 80}ms`
  }} className={`group relative flex flex-col rounded-lg border border-border bg-card p-8 sm:p-10 transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-lg ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="mb-8">
        
      </div>

      <h3 className="font-default text-2xl font-semibold text-foreground mb-3">
        {service.title}
      </h3>

      <p className="font-default text-[15px] leading-relaxed text-foreground/75 mb-8 flex-1">
        {service.description}
      </p>

      <Link to="/OurCleaningMenuPage" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-300">
        <span>Get an Instant Quote</span>
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>;
}
export default function TheCleaningMenu() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="668" id="the-cleaning-menu" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20 lg:mb-24 transition-all duration-700 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-6 flex flex-col">
            <span className="font-default text-xs tracking-[0.2em] uppercase text-primary mb-6">
              Our Services
            </span>
            <h2 className="font-default text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] font-semibold text-foreground tracking-tight">
              A <span className="italic font-serif">pristine</span> home, every single time.
            </h2>
            <p className="font-default text-base md:text-lg leading-relaxed text-foreground/75 mt-8 max-w-xl">
              From deep seasonal refreshes to regular maintenance, our professional team ensures your home remains a sanctuary of cleanliness and comfort.
            </p>
          </div>

          <div className="lg:col-span-6 lg:pt-24">
            <p className="font-default text-base md:text-lg leading-relaxed text-foreground/80 max-w-xl">
              At Sparkle & Shine Maids, we believe a clean home is the foundation of a peaceful life. Our meticulous approach and commitment to quality mean you can spend less time worrying about chores and more time enjoying your space.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <ServiceCard key={service.title} service={service} index={i} />)}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex justify-center">
          <Link to="/OurCleaningMenuPage" className="group inline-flex items-center gap-2 font-default text-sm tracking-wide text-primary hover:text-primary/80 transition-colors duration-300">
            <span className="border-b border-primary/30 group-hover:border-primary/50 pb-1 transition-colors duration-300">
              View all cleaning packages
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}